import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  isRTL,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const DRAG_THRESHOLD = 5;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
              // Ce qui a été mis de côté faute d'être visible se rattrape ici.
              slideshowElement.catchUpMeasurement?.();
              // `play` sort tout seul si une lecture est déjà en cours, et `paused`
              // reste la décision du visiteur : on ne relance que ce qu'on a suspendu.
              if (slideshowElement.autoplay && !slideshowElement.paused) slideshowElement.play();
            } else {
              slideshowElement.removeAttribute('in-viewport');
              slideshowElement.suspend();
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    /* Page posée : ce qui est à l'écran rattrape sa mesure, l'observateur s'occupe du reste. */
    if (document.readyState !== 'complete') {
      window.addEventListener(
        'load',
        () => {
          if (this.hasAttribute('in-viewport')) this.catchUpMeasurement();
        },
        { once: true }
      );
    }

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    this.#hasProgressBar = null;
    this.#fillLabels();
    this.#watchMetrics();
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  /**
   * @returns {boolean} Vrai si le travail a été mis de côté.
   */
  #deferMeasurement() {
    if (this.#alreadyMeasured) return false;

    /*
     * La section de tête se mesure tout de suite : c'est celle qu'on voit, et sa barre
     * de progression doit être juste dès la première image.
     */
    if (this.#isAtPageStart()) return false;

    /*
     * Pour toutes les autres, rien avant que la page soit chargée.
     *
     * Se fier à « est-ce visible » ne suffit pas : pendant le chargement les sections
     * sont encore tassées les unes sur les autres, faute d'images dimensionnées, et
     * l'observateur les déclare presque toutes visibles. Elles se mesuraient donc
     * quand même — 300 ms de recalcul forcé pour la seule mesure de piste, relevés à
     * la trace. Une fois la page posée, les positions sont vraies et l'approche suffit.
     */
    if (document.readyState !== 'complete' || !this.hasAttribute('in-viewport')) {
      this.#pendingMeasurement = true;
      return true;
    }

    return false;
  }

  /** Le carrousel est-il dans la première section de la page ? Aucune mesure ici. */
  #isAtPageStart() {
    const section = this.closest('.shopify-section');
    return section instanceof HTMLElement && section.previousElementSibling === null;
  }

  /** @type {boolean} Une mesure a-t-elle été mise de côté faute d'être visible ? */
  #pendingMeasurement = false;

  /** @type {string|undefined} Dernière boîte mesurée, pour ne pas remesurer à l'identique. */
  #boxFingerprint = undefined;

  /** @type {number|undefined} Image demandée pour la reprise groupée. */
  #resumeRequested = undefined;

  /** @type {boolean} Le carrousel a-t-il déjà été mesuré une fois pour de bon ? */
  #alreadyMeasured = false;

  /** Rattrape la mesure mise de côté, à l'entrée dans le champ de vision. */
  catchUpMeasurement() {
    if (!this.#pendingMeasurement) return;

    this.#pendingMeasurement = false;
    this.#alreadyMeasured = true;

    /*
     * L'ORDRE COMPTE : on lit d'abord, on écrit ensuite.
     *
     * `#measure` pose des variables CSS ; la mesure de piste qui suivait devait alors
     * attendre un recalcul complet, et le découpage un second. En rangeant les lectures
     * devant, il n'en reste qu'un. `#calibrateMarks` publie déjà l'avancement en
     * terminant : l'appeler une fois de plus ne faisait que redemander la position.
     */
    this.#measureTrack(true);
    this.#calibrateMarks();
    // `#measure` n'est posé qu'une fois les métriques installées ; le rattrapage peut
    // arriver avant, précisément parce que cette installation est elle aussi différée.
    this.#measure?.();
  }

  /** @type {ResizeObserver | null} */
  #metricsObserver = null;
  /** @type {(() => void) | null} */
  #measure = null;

  /* Publie ce que le CSS ne sait pas calculer, pour les diaporamas dont le contenu se
     cale sur le média : la hauteur réelle de celui-ci, et la place prise par la rangée
     de commandes posée dessus. */
  #watchMetrics() {
    /** @type {HTMLElement | null} */
    const media = this.querySelector('.slide__media');
    if (!media) return;

    /** @type {HTMLElement | null} */
    const bar = this.querySelector('.slideshow__controls');

    this.#measure = () => {
      if (bar) {
        const shown = bar.offsetParent !== null;
        const offset = shown ? parseFloat(getComputedStyle(bar).bottom) || 0 : 0;
        this.style.setProperty('--nav-space', shown ? `${Math.round(bar.offsetHeight + offset)}px` : '0px');
      }
      this.style.setProperty('--media-height', `${Math.round(media.offsetHeight)}px`);
    };

    // Les boîtes observées couvrent les deux mesures ; le redimensionnement de
    // la fenêtre s'y ajoute, car changer de largeur peut changer la règle qui
    // s'applique sans changer la boîte observée à cet instant.
    this.#metricsObserver = new ResizeObserver(() => {
      if (this.#deferMeasurement()) return;
      this.#measure?.();
    });
    for (const node of [this.refs.scroller, bar, media]) {
      if (node) this.#metricsObserver.observe(node);
    }
    window.addEventListener('resize', this.#measure, { passive: true });
    if (!this.#deferMeasurement()) this.#measure?.();
  }

  /* Pagination « libellés » : le bouton reprend le titre de sa diapo. */
  #fillLabels() {
    const labels = this.querySelectorAll('.slideshow-controls__label');
    if (!labels.length) return;

    /** @type {HTMLElement[]} */
    const slides = this.slides ?? [];
    for (const [index, button] of labels.entries()) {
      const slide = slides[index];
      if (!slide) continue;

      const chosen = slide.dataset.slideLabel?.trim();
      let text = chosen;
      if (!text) {
        const source =
          slide.querySelector('.slide__content .text-block') ?? slide.querySelector('h1, h2, h3, h4, h5, h6');
        text = source?.textContent?.trim().replace(/\s+/g, ' ') ?? '';
        if (text.length > 40) text = `${text.slice(0, 39).trimEnd()}\u2026`;
      }
      if (text) button.textContent = text;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    this.#metricsObserver?.disconnect();
    this.#metricsObserver = null;
    if (this.#measure) window.removeEventListener('resize', this.#measure);
    this.#measure = null;

    this.refs.scroller?.removeEventListener('scroll', this.#publishProgress);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
        slide.toggleAttribute('inert', true);
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
          requestedSlide.toggleAttribute('inert', false);
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');
    slide.toggleAttribute('inert', false);

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    this.paused = false;

    /*
     * Quand un trait mène la lecture, il EST la minuterie : en ouvrir une seconde
     * ferait avancer les images deux fois.
     */
    if (!this.#strokeAnimation) this.#armStroke();

    if (this.#strokeAnimation) {
      this.#strokeAnimation.play();
      return;
    }

    if (this.#interval) return;

    this.#interval = setInterval(() => {
      // Le survol met la lecture en pause — mais pas quand il porte sur les commandes :
      // sinon le bouton « lecture » ne repartait jamais, le curseur restant dessus.
      if (document.hidden) return;
      if (
        !this.hasAttribute('no-pause-on-hover') &&
        this.matches(':hover') &&
        !this.querySelector('slideshow-controls:hover')
      ) {
        return;
      }

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
    this.#strokeAnimation?.pause();
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    /*
     * Reprendre, c'est repartir d'où l'on s'est arrêté. `pause()` puis `play()`
     * remettait le compte à rebours à zéro — et sur un trait, changeait d'image.
     */
    if (this.#strokeAnimation) {
      this.#strokeAnimation.play();
      return;
    }

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    const aChange = this.#current !== value;
    this.#current = value;

    // La lecture d'abord, les écritures ensuite — et une seule lecture de position,
    // partagée avec le marquage des repères plus bas.
    this.#publishProgress();
    /*
     * La page se déduit ici de la DIAPO courante, jamais de la position de la piste.
     *
     * C'est la bonne source à cet instant précis — la diapo vient de changer, la piste
     * la rejoint ensuite — et surtout c'est un simple calcul : lire la position aurait
     * obligé le navigateur à recalculer toute la mise en page, une fois par carrousel.
     * Le défilement, lui, continue d'affiner la page dans `#publishProgress`.
     */
    const page = this.#rankBySlide + 1;

    if (current) current.textContent = `${Math.floor(value / Math.max(1, this.#markedSteps)) + 1}`;

    // Les vignettes suivent la diapo ; les repères de pagination suivent la PAGE.
    thumbnails?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    this.#publishPage(page);

    for (const [i, slide] of (this.refs.slides ?? []).entries()) {
      if (!(slide instanceof HTMLElement)) continue;

      const active = i === value;

      /*
       * L'apparition du contenu est une entrée, pas une transition : elle ne doit jouer
       * qu'à la première venue de la diapo. Une diapo qu'on quitte est marquée comme
       * déjà vue, et le CSS cesse alors de l'animer — sinon le contenu rejouait son
       * entrée à chaque retour sur la première image.
       */
      if (!active && slide.hasAttribute('data-active')) slide.setAttribute('data-seen', '');

      slide.toggleAttribute('data-active', active);
    }

    /*
     * L'horloge s'arme APRÈS que le repère actif a bougé : en style Segments, elle
     * anime le remplissage du segment courant, et il faut donc savoir lequel l'est.
     * Armée trop tôt, elle remplissait le segment sortant et le nouveau restait vide.
     *
     * Et elle ne s'arme que sur un vrai changement de PAGE. Pendant une transition, la
     * diapo courante peut osciller le temps que le défilement se cale : chaque passage
     * annulait l'animation en cours et en repartait une nouvelle, si bien que le
     * remplissage restait proche de zéro et que l'image changeait trop tôt.
     */
    if (aChange) this.#armStroke();

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }



  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  /** @type {number} Nombre de diapos par page lors du dernier calibrage. */
  #markedSteps = 1;

  /** @type {number} Nombre de pages — l'unité dans laquelle la barre est dessinée. */
  #pageCount = 1;

  #calibrateMarks() {
    const { dots, slides, current: counter, scroller } = this.refs;
    if (!dots?.length || !slides?.length || !(scroller instanceof HTMLElement)) return;

    const totalLength = scroller.scrollWidth;
    const viewport = scroller.clientWidth;

    // Sans mise en page, tout mesure zéro : on ne fige pas un découpage sur ce néant.
    if (viewport <= 0) return;

    const travel = Math.max(0, totalLength - viewport);
    const first = slides[0] instanceof HTMLElement ? slides[0] : null;
    // En RTL la piste avance vers la gauche : le décalage se mesure entre bords droits.
    const rtl = isRTL(scroller);
    /** @param {HTMLElement} slide */
    const offsetOf = (slide) =>
      !first
        ? 0
        : rtl
          ? first.offsetLeft + first.offsetWidth - (slide.offsetLeft + slide.offsetWidth)
          : slide.offsetLeft - first.offsetLeft;

    /** @type {number[]} */
    const starts = [];
    for (const [index, slide] of slides.entries()) {
      if (!(slide instanceof HTMLElement)) continue;
      if (offsetOf(slide) < travel) starts.push(index);
    }

    // Le dernier arrêt, c'est le bout de la piste : il revient à la dernière diapo.
    const latest = slides.length - 1;
    if (!starts.includes(latest)) starts.push(latest);
    if (!starts.length) starts.push(0);

    const fingerprint = starts.join(',');
    if (fingerprint === this.#slicing) return;
    this.#slicing = fingerprint;
    this.#markedSteps = Math.max(1, Math.round(slides.length / starts.length));

    /*
     * TOUTES LES LECTURES D'ABORD, LES ÉCRITURES ENSUITE.
     *
     * Mesurer la piste oblige le navigateur à recalculer la mise en page si un style a
     * été écrit depuis la mesure précédente. En alternant les deux, cette méthode le
     * lui demandait trois fois — et elle tourne une fois par carrousel, sur une page
     * d'accueil qui en compte une trentaine : 155 ms de recalcul au chargement, relevés
     * au profileur, en plein pendant l'animation d'apparition du diaporama.
     */
    this.#pageCount = starts.length;
    this.#pageStarts = starts;
    this.#stopsInPixels = starts.map((index) => {
      const slide = slides[index];
      const offset = slide instanceof HTMLElement ? offsetOf(slide) : 0;
      return Math.min(Math.max(0, offset), travel);
    });

    const row = dots[0] instanceof HTMLElement ? dots[0].closest('ol, .slideshow-controls__progress') : null;
    const total = this.querySelector('.slideshow-controls__counter [data-total]');
    /*
     * La page se déduit ici de la DIAPO courante, jamais de la position de la piste.
     *
     * C'est la bonne source à cet instant précis — la diapo vient de changer, la piste
     * la rejoint ensuite — et surtout c'est un simple calcul : lire la position aurait
     * obligé le navigateur à recalculer toute la mise en page, une fois par carrousel.
     * Le défilement, lui, continue d'affiner la page dans `#publishProgress`.
     */
    const page = this.#rankBySlide + 1;

    // — à partir d'ici, plus aucune mesure —

    for (const [i, mark] of dots.entries()) {
      if (!(mark instanceof HTMLElement)) continue;

      const rank = starts.indexOf(i);

      // Le repère porte parfois un habillage (un <li>) : c'est lui qu'il faut
      // retirer, sinon sa puce ou son écart subsiste.
      const target = mark.closest('li') ?? mark;
      target.hidden = rank < 0;
      if (rank < 0) continue;

      // Le repère porte son rang de PAGE : c'est l'unité dans laquelle la barre est
      // dessinée, et Liquid l'avait numéroté en diapos.
      target.style.setProperty('--segment-index', String(rank));
      if (mark.classList.contains('slideshow-controls__number')) {
        mark.textContent = String(rank + 1).padStart(2, '0');
      }
    }

    if (row instanceof HTMLElement) row.style.setProperty('--segment-count', String(starts.length));

    // Compteur « 1/5 » : le dénominateur compte lui aussi des pages.
    if (total instanceof HTMLElement) total.textContent = String(starts.length);
    if (counter instanceof HTMLElement) counter.textContent = String(page);
    this.#publishPage(page);

    // Les repères viennent de changer d'unité : l'avancement ET la page doivent être
    // republiés, même si le rang n'a pas bougé — il ne désigne plus la même chose.
    this.#progressWritten = null;
    this.#publishedPage = null;
    this.#publishProgress();
  }

  /**
   * LE TRAIT DE LECTURE AUTOMATIQUE — c'est lui qui mène.
   *
   * Une seule horloge : l'animation du trait. Quand elle arrive au bout, on passe à
   * l'image suivante et on la relance. La barre atteint donc exactement le bout au
   * moment du changement, par construction, et la pause la fige où elle en est.
   * Une minuterie séparée dériverait par rapport au trait, ne s'arrêterait pas avec lui
   * et repartirait de zéro à la reprise.
   *
   * @returns {HTMLElement | null} Le trait, créé au besoin.
   */
  #stroke() {
    const row = this.querySelector('.slideshow-controls__progress--line');

    /*
     * Style « Barre » : un trait unique posé par-dessus, qui parcourt toute la longueur.
     */
    if (row instanceof HTMLElement) {
      const existing = row.querySelector(':scope > .slideshow-controls__stroke');
      if (existing instanceof HTMLElement) return existing;

      const stroke = document.createElement('li');
      stroke.className = 'slideshow-controls__stroke';
      stroke.setAttribute('aria-hidden', 'true');
      row.append(stroke);
      return stroke;
    }

    /*
     * Style « Segments » : c'est le remplissage du segment courant qui tient l'horloge.
     * Sans ça, seul le style Barre était piloté par l'animation ; les segments gardaient
     * la minuterie séparée et l'image changeait avant la fin du remplissage.
     */
    const segments = this.querySelector('.slideshow-controls__progress--segments');
    if (!(segments instanceof HTMLElement)) return null;

    /*
     * Le segment est choisi sur l'INDICE DE DIAPO, pas sur `aria-selected`.
     *
     * Ce dernier suit la position de la piste, qui met le temps du défilement à
     * rejoindre la diapo demandée : au moment où l'horloge s'arme, il désigne encore le
     * segment sortant. On remplissait donc celui qu'on quitte, et le nouveau restait à
     * zéro — visible en ligne, invisible sur un banc où la piste se cale plus vite.
     */
    const rank = this.#rankBySlide;

    const visible = [...segments.children].filter(
      (li) => li instanceof HTMLElement && getComputedStyle(li).display !== 'none'
    );
    const current = visible[rank]?.querySelector('.slideshow-controls__progress-fill');
    return current instanceof HTMLElement ? current : null;
  }

  /** @type {Animation | undefined} */
  #strokeAnimation = undefined;

  /** Arme le trait pour la diapo en cours, et le remet à zéro. */
  #armStroke() {
    if (!this.autoplay) return;

    const stroke = this.#stroke();
    if (!stroke) return;

    this.#strokeAnimation?.cancel();

    const animation = stroke.animate([{ scale: '0 1' }, { scale: '1 1' }], {
      duration: this.autoplayInterval,
      easing: 'linear',
      fill: 'both',
    });

    animation.onfinish = () => {
      // Le trait est au bout : c'est le moment d'avancer, et de repartir.
      if (this.paused) return;
      this.next();
    };

    this.#strokeAnimation = animation;
    if (this.paused) animation.pause();

    /*
     * Une minuterie a pu être ouverte avant que les commandes ne soient rendues — le
     * trait n'existait pas encore. Les deux horloges tournaient alors ensemble et la
     * plus rapide changeait l'image avant la fin du remplissage : mesuré, le trait
     * n'était qu'à 0,76 au changement. Le trait est seul maître à partir d'ici.
     */
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /** @type {string} Empreinte du découpage en pages, pour ne pas le refaire pour rien. */
  #slicing = '';

  /** @type {number[]} Index de la diapo qui commence chaque page. */
  #pageStarts = [0];

  /**
   * Numéro de la page affichée, à partir de 1 — déduit de la POSITION de la piste.
   *
   * Il se déduisait de la diapo courante. Or quand une carte est plus large que la
   * course restante, aucune autre ne peut atteindre le bord gauche : la diapo courante
   * ne change jamais, la page restait à 1 et le deuxième segment ne s'allumait pas,
   * même arrivé au bout. Mesuré sur « Collection en vedette » en desktop : 5 cartes de
   * 648 px pour 616 px de course.
   *
   * La position, elle, dit toujours où l'on en est.
   */
  /**
   * Rang de la page pour une position DÉJÀ LUE.
   *
   * Séparé du lecteur ci-dessous parce que lire la position coûte cher : c'est une
   * mesure, et le navigateur doit recalculer toute la mise en page si un style a été
   * écrit depuis la lecture précédente. La publication de l'avancement écrivait des
   * variables CSS entre deux lectures — 248 ms de recalcul au chargement, mesurés au
   * profileur. Elle ne lit plus qu'une fois et passe la valeur ici.
   *
   * @param {number} position
   * @returns {number}
   */
  #rankOfPosition(position) {
    const stops = this.#stopsInPixels;
    if (!stops.length) return 1;

    let rank = 0;
    for (const [i, stopPosition] of stops.entries()) {
      // Une demi-diapo de tolérance : la page bascule quand on l'a franchie pour de bon.
      if (position >= stopPosition - 1) rank = i;
    }
    return rank + 1;
  }

  get #currentPage() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return 1;

    return this.#rankOfPosition(
      Math.abs(this.#scroll?.axis === 'y' ? scroller.scrollTop : scroller.scrollLeft)
    );
  }

  /** @type {number[]} Position de défilement de chaque arrêt. */
  #stopsInPixels = [];


  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Dernier taux d'intersection connu de chaque diapo.
   * @type {Map<Element, number>}
   */
  #slideRatios = new Map();

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
      // Zéro ou une diapo : verdict définitif, quel que soit le cadre.
      this.toggleAttribute('controls-hidden', true);
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
      this.refs.slides[0].toggleAttribute('inert', false);
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      // La piste d'un diaporama défile toujours à l'horizontale : le déclarer
      // évite quatre lectures de mise en page par carrousel, répétées à chaque
      // défilement programmé.
      axis: 'x',
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    // La rampe publie l'avancement elle-même, avec la position qu'elle vient d'écrire.
    this.#scroll.followsRamp(this.#publishFromRamp);

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    scroller.addEventListener('scroll', this.#publishProgress, { passive: true });

    if (!this.hasAttribute('no-pause-on-hover')) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
    }
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async (entries) => {
        if (viewTransition.current) await viewTransition.current;

        // Loin de l'écran, rien à calculer : tout est rattrapé à l'approche.
        if (this.#deferMeasurement()) return;

        /*
         * UNE SEULE REPRISE PAR IMAGE, ET SEULEMENT SI LA BOÎTE A BOUGÉ.
         *
         * L'observateur se déclenche à chaque changement de taille : au chargement,
         * chaque image qui arrive rallonge la piste et relance mesure, découpage et
         * publication. Une trentaine de carrousets, plusieurs reprises chacun — c'est
         * ce qui faisait 690 ms de recalcul forcé sur un mobile au processeur ralenti.
         *
         * Les reprises d'une même image sont donc fondues en une seule, et une boîte
         * identique à la précédente ne relance rien du tout.
         *
         * La boîte est prise dans l'ENTRÉE de l'observateur, jamais relue dans le
         * document : l'observateur l'a déjà calculée, alors que la relire redemanderait
         * précisément le recalcul qu'on cherche à éviter.
         */
        const box = entries[0]?.contentRect;
        if (box) {
          const fingerprint = `${Math.round(box.width)}x${Math.round(box.height)}`;
          if (fingerprint === this.#boxFingerprint) return;
          this.#boxFingerprint = fingerprint;
        }

        if (this.#resumeRequested !== undefined) cancelAnimationFrame(this.#resumeRequested);
        await new Promise((done) => {
          this.#resumeRequested = requestAnimationFrame(() => {
            this.#resumeRequested = undefined;
            done(undefined);
          });
        });

        // La part déjà vue dépend de la largeur de la fenêtre de piste : elle change
        // à chaque redimensionnement, et la toute première mesure au montage tombe
        // avant que la mise en page soit arrêtée.
        this.#measureTrack(true);

        /*
         * Le découpage en pages dépend de la même géométrie. Il n'était recalculé que
         * depuis l'observateur de visibilité — donc jamais au redimensionnement, ni
         * quand les images finissent de charger et allongent la piste. Il restait figé
         * sur la mesure provisoire du montage : une section pouvait afficher quatre
         * repères là où le défilement n'offre qu'un seul arrêt.
         */
        this.#calibrateMarks();
        requestAnimationFrame(() => this.#publishProgress());

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Dimensions de la piste, mesurées une fois puis conservées. Elles ne
   * changent qu'au redimensionnement, alors qu'un défilement peut produire des
   * dizaines d'événements par seconde : les relire à chaque fois, juste après
   * l'écriture de la propriété d'avancement, revenait à réclamer un recalcul
   * de mise en page à chaque image, sur chacun des carrousels de la page.
   * @type {{ total: number, viewport: number } | null}
   */
  #trackSize = null;

  /** @type {boolean} Une mesure a déjà été tentée dans l'image en cours. */
  #trackMeasuredThisFrame = false;

  #measureTrack(force = false) {
    const { scroller } = this.refs;
    if (!scroller) return null;

    // Tant que la piste n'a pas de dimensions, la mesure n'est pas retenue et chaque
    // événement de défilement la relançait — juste après l'écriture de `--slideshow-
    // progress`, donc en recalcul forcé.
    if (this.#trackMeasuredThisFrame && !force) return this.#trackSize;
    this.#trackMeasuredThisFrame = true;
    requestAnimationFrame(() => {
      this.#trackMeasuredThisFrame = false;
    });

    const horizontal = this.#scroll?.axis !== 'y';
    const measurements = {
      total: horizontal ? scroller.scrollWidth : scroller.scrollHeight,
      viewport: horizontal ? scroller.clientWidth : scroller.clientHeight,
    };

    // Une piste sans mise en page mesure zéro : la retenir figerait l'avancement à sa
    // valeur de repli pour de bon.
    if (measurements.total > 0 && measurements.viewport > 0) {
      this.#trackSize = measurements;
      this.#publishStart();
    }

    return measurements;
  }

  /**
   * Durée d'un intervalle de lecture automatique, pour le compte à rebours de la barre.
   * Elle ne dépend pas de la piste : seul le réglage du marchand la fait changer.
   */
  #publishStart() {
    if (!this.autoplay) return;

    // Une diapo peut contenir son propre carrousel : on ne prend que la barre qui
    // appartient à CE composant, jamais celle d'un composant imbriqué.
    const bar = [...this.querySelectorAll('.slideshow-controls__progress')].find(
      (candidate) => candidate.closest('slideshow-component') === this
    );
    if (!bar) return;

    const duration = `${this.autoplayInterval}ms`;
    if (duration === this.#publishedDuration) return;
    this.#publishedDuration = duration;

    /* `--duree-lecture-auto` n'etait lue par aucune feuille. */
  }

  /** @type {string | null} Dernière durée publiée. */
  #publishedDuration = null;

  /** @type {boolean | null} Une barre de progression est-elle présente ? */
  #hasProgressBar = null;

  /** @type {number|undefined} Position donnée par la rampe, le temps d'une image. */
  #announcedPosition = undefined;

  /**
   * Publie l'avancement à partir d'une position DONNÉE, sans rien relire.
   * @param {number} position
   */
  #publishFromRamp = (position) => {
    this.#announcedPosition = Math.abs(position);
    try {
      this.#publishProgress();
    } finally {
      this.#announcedPosition = undefined;
    }
  };

  #publishProgress = () => {
    // Une rampe mène la piste : c'est elle qui publie, pas l'écouteur de défilement.
    if (this.#announcedPosition === undefined && this.#scroll?.onRamp) return;

    const { scroller } = this.refs;
    if (!scroller) return;

    /*
     * Le garde couvre TOUTES les rangées de repères, pas seulement la barre.
     *
     * Il ne cherchait que `.slideshow-controls__progress`, qui n'existe ni en style
     * Points ni en style Numéros : la publication s'arrêtait là, et avec elle le calcul
     * de la page courante. Le repère actif ne se remettait donc à jour qu'au changement
     * de diapo — or les flèches ne changent pas toujours de diapo quand une carte est
     * plus large que la course restante. Le point restait alors figé à gauche, puis
     * sautait au retour.
     */
    this.#hasProgressBar ??=
      this.querySelector(
        '.slideshow-controls__progress, .slideshow-controls__dots, .slideshow-controls__numbers'
      ) != null;
    if (!this.#hasProgressBar) return;

    const measurements = this.#trackSize ?? this.#measureTrack();
    if (!measurements) return;
    const { total, viewport } = measurements;

    // Piste plus courte que sa fenêtre : tout est déjà visible, donc terminé.
    if (total - viewport <= 1) {
      // Tout tient à l'écran : une seule page, la barre est pleine.
      this.#queueProgress(String(this.#pageCount));
      return;
    }

    /*
     * Pendant une rampe, la position vient de la rampe elle-même : la relire juste après
     * qu'elle l'a écrite obligerait le navigateur à refaire la mise en page, une fois
     * par image.
     */
    const horizontal = this.#scroll?.axis !== 'y';
    const position =
      this.#announcedPosition ?? (horizontal ? Math.abs(scroller.scrollLeft) : scroller.scrollTop);

    /*
     * L'avancement, exprimé en PAGES : 1 quand la première page est à l'écran, le
     * nombre de pages quand on atteint le bout. C'est l'unité dans laquelle la barre
     * est dessinée — un segment par page.
     */
    const progress = clamp(position / (total - viewport), 0, 1);

    // Les segments basculent sur la page, et la page suit la position : elle doit donc
    // être republiée au fil du défilement, pas seulement au changement de diapo.
    // Déduite AVANT toute écriture de style, sinon la position devrait être relue.
    const page = String(this.#rankOfPosition(position));

    this.#queueProgress((1 + progress * (this.#pageCount - 1)).toFixed(4));
    this.#publishPage(Number(page));
  };

  refreshProgress() {
    this.#measureTrack(true);
    requestAnimationFrame(() => {
      this.#publishProgress();
      // Les diapos viennent de changer : le verdict d'inertie aussi.
      this.#updateControlsVisibility();
    });
  }

  /**
   * Marque le repère de la page affichée.
   *
   * Il ne l'était qu'au changement de diapo. Or la page, elle, suit la POSITION : sur
   * un carrousel où une carte est plus large que la course restante, on peut parcourir
   * toute la piste sans jamais changer de diapo — le point actif restait alors sur le
   * premier, et se décalait au retour. Il se met désormais à jour partout où la page
   * change, c'est-à-dire au fil du défilement.
   */
  /**
   * Rang de la page d'après l'INDICE DE DIAPO.
   *
   * `#currentPage` se déduit de la position de la piste, ce qui est juste quand c'est
   * le visiteur qui fait défiler. En lecture automatique c'est l'inverse : la diapo est
   * décidée d'abord, la piste la rejoint ensuite. Marquer le repère sur la position
   * désignait alors le segment sortant pendant que l'horloge remplissait le suivant —
   * un segment courant vide, un segment à venir qui se remplit.
   */
  get #rankBySlide() {
    let rank = 0;
    for (const [i, pageStart] of this.#pageStarts.entries()) {
      if (pageStart <= this.#current) rank = i;
    }
    return rank;
  }

  /**
   * @param {number} [knownPage] Rang de page déjà calculé par l'appelant, à partir
   *   d'une position lue avant toute écriture de style. Sans lui, il faudrait relire la
   *   position de la piste — donc refaire calculer toute la mise en page.
   */
  #marksPage(knownPage) {
    const { dots } = this.refs;
    if (!dots?.length) return;

    const page = knownPage ?? this.#currentPage;
    const rank = this.autoplay ? this.#rankBySlide : page - 1;
    const opensPage = this.#pageStarts[rank] ?? 0;
    for (const [index, marker] of dots.entries()) {
      if (marker instanceof HTMLElement) marker.setAttribute('aria-selected', `${index === opensPage}`);
    }
  }

  /** @type {string | null} Dernière page publiée. */
  #publishedPage = null;

  /**
   * Publie le rang de la page affichée : la variable que lit la barre, et les repères.
   *
   * Un seul écrivain, parce qu'ils étaient deux. Le changement de diapo écrivait la
   * variable sans tenir le journal de ce qui avait été publié ; le défilement, lui, s'y
   * fiait pour décider s'il fallait republier. Arrivé au bout d'un carrousel dont la
   * dernière page montre plusieurs cartes, la position disait « dernière page » alors
   * que le journal l'annonçait déjà publiée : la correction était sautée, et le dernier
   * segment restait vide, le dernier point non retenu.
   *
   * @param {number} page
   */
  #publishPage(page) {
    const rawValue = String(page);
    if (rawValue === this.#publishedPage) return;

    this.#publishedPage = rawValue;
    this.style.setProperty('--current-page', rawValue);
    this.#marksPage(page);
  }

  /** @type {string | null} Dernière valeur écrite, pour ne pas réécrire à l'identique. */
  #progressWritten = null;

  /**
   * Écriture IMMÉDIATE de l'avancement.
   *
   * Elle était reportée à l'image suivante pour grouper les écritures — mais rien
   * n'est lu après, donc le report n'achetait rien et coûtait une image de retard :
   * la valeur calculée depuis l'événement de défilement était peinte alors que la
   * piste avait déjà avancé. Mesuré, la barre retardait de 1,2 % en moyenne et
   * jusqu'à 5 % de sa longueur — l'à-coup visible sur la ligne et les segments.
   *
   * Un événement de défilement est distribué avant la peinture de son image :
   * écrire ici, c'est peindre la bonne valeur dans la bonne image. Aucune mesure
   * n'est relue au passage (les dimensions de piste sont en cache), donc pas de
   * recalcul de mise en page forcé.
   * @param {string} rawValue
   */
  #queueProgress(rawValue) {
    if (rawValue === this.#progressWritten) return;
    this.#progressWritten = rawValue;

    this.style.setProperty('--page-progress', rawValue);
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    // Lectures groupées avant toute écriture.
    const previousIndex = this.#current;
    const index = this.#measureIndex();

    this.#publishProgress();
    this.current = index;

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.#publishProgress();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  /* Mesure seule : rend l'index de la diapo la plus proche du repère, sans rien écrire. */
  #measureIndex = () => {
    const { slides } = this;
    if (!slides) return 0;

    if (!this.#scroll) return 0;

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return 0;

    return slides.indexOf(closestVisibleSlide);
  };

  /** Mesure puis enregistre. */
  #sync = () => (this.current = this.#measureIndex());

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (event.button !== 0) return;
    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const clickController = new AbortController();
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!moved && (!initialDelta || Math.abs(initialDelta) < DRAG_THRESHOLD)) return;

      if (!moved) {
        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        moved = true;
        this.#dragging = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal: clickController.signal });

        this.pause();
        this.setAttribute('dragging', '');
        this.#scroll.snap = false;
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      if (event.type !== 'pointerup') clickController.abort();
      this.#dragging = false;

      if (!moved) return;

      const { current, slides } = this;
      const { scroller } = this.refs;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(scroller instanceof HTMLElement)) return;

    // Tiroir fermé, bloc replié : tout mesure zéro et « rien ne déborde » serait un faux
    // verdict, figé jusqu'au prochain recalcul.
    if (scroller.offsetWidth <= 0) return;

    const inert = scroller.scrollWidth <= scroller.offsetWidth;
    if (slideshowControls instanceof HTMLElement) slideshowControls.hidden = inert;

    // L'attribut porte le verdict pour le CSS : l'attribut `hidden` seul perd
    // contre toute règle d'affichage auteur, et les flèches logées dans
    // l'EN-TÊTE ne sont pas dans la barre de commandes — seul le CSS peut
    // les atteindre toutes.
    this.toggleAttribute('controls-hidden', inert);
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
    this.#slideRatios.clear();

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);

          // Un appel ne porte que sur les diapos qui viennent de franchir un
          // seuil : on garde le dernier taux connu de chacune pour pouvoir
          // raisonner sur l'ensemble, une fois la boucle finie.
          this.#slideRatios.set(slide, entry.intersectionRatio);

          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));

        this.#calibrateMarks();

        // Une diapo entièrement hors champ est retirée aux technologies d'assistance
        // ET au clavier.
        const observed = [...this.#slideRatios.keys()];
        const anyInFrame = observed.some((slide) => (this.#slideRatios.get(slide) ?? 0) > 0);
        for (const slide of observed) {
          const outOfFrame = anyInFrame && (this.#slideRatios.get(slide) ?? 0) === 0;
          slide.toggleAttribute('inert', outOfFrame);
          slide.setAttribute('aria-hidden', `${outOfFrame}`);
        }

        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        // Deux seuils : celui de la visibilité, et zéro pour savoir quand une
        // diapo quitte complètement le champ.
        threshold: [0, SLIDE_VISIBLITY_THRESHOLD],
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
